import * as React from 'react';

import { Dispatch } from 'redux';
import { connect } from 'react-redux';
import { useHistory, useLocation } from 'react-router';
import { CallHistoryMethodAction, push } from 'connected-react-router';

import { Translate } from 'react-localize-redux';

import { rgba } from 'polished';
import styled from 'styled-components';

import AppState from '@webapp/store/state';

import Flex from '@components/layout/flex';
import Typography from '@components/basics/typography';
import SquareImg from '@components/basics/artwork-img';
import MotionOpacity from '@components/motion/motion-opacity';
import FormContainer from '@components/layout/form-container';
import SidebarContainer from '@components/layout/sidebar-container';
import { getMainDomain } from '@webapp/utils/get-artist-site-url';
import * as merch from '@webapp/sdk/merch';

const mockupMerchImg =
  'https://images-api.printify.com/mockup/63370e058f8fb4121e4c67ef/22073/32/unisex-ultra-cotton-tee.jpg';

interface MerchPublishSuccessPageProps {
  className?: string;
  isMobile?: boolean;
  artistId?: string;
  baseUrl: string;
  continueToCatalog: (
    baseUrl: string,
    isSampleOrdered: boolean
  ) => CallHistoryMethodAction;
  goTo: (path: string) => CallHistoryMethodAction;
}

const mapStateToProps = (state: AppState) => {
  const subdomain =
    state.subdomain.currentSubdomain?.subdomain ||
    state.artist.active?.shortId ||
    '';
  return {
    isMobile: state.ui.mobile,
    baseUrl: `https://${subdomain}.${getMainDomain()}/`,
    artistId: state.artist.active?.id as string,
  };
};

const mapDispatchToProps = (dispatch: Dispatch) => {
  return {
    continueToMerchPage: (baseUrl: string, hasProductSample: boolean) => {
      if (hasProductSample) {
        dispatch(push(`/catalog?type=merch&status=processing`));
      } else {
        window.open(`${baseUrl}merch`, '_blank');
        dispatch(push(`/catalog?type=merch`));
      }
    },
    goTo: (url: string) => dispatch(push(url)),
  };
};

const MerchPublishSuccess = (props: MerchPublishSuccessPageProps) => {
  const [product, setProduct] = React.useState<
    | {
        merchName: string | undefined;
        merchImg: string;
      }
    | undefined
  >();
  const { continueToCatalog, goTo, baseUrl, artistId } = props;
  const history = useHistory();
  const urlParams = new URLSearchParams(useLocation().search);
  const productId = urlParams.get('product-id');
  const orderId = urlParams.get('order-id');

  function redirectCatalog() {
    history.push('/catalog?type=merch&status=processing');
  }

  React.useEffect(() => {
    if (baseUrl && orderId) {
      setTimeout(function () {
        continueToCatalog(baseUrl, !!orderId);
      }, 50000);
    }
  }, [baseUrl, orderId, continueToCatalog]);

  React.useEffect(() => {
    let isRendered = true;
    const loadProduct = async (artistId: string, productId) => {
      const merchProduct = await merch.getProduct(artistId, productId);
      if (!merchProduct) {
        goTo('/dashboard');
      }
      if (isRendered) {
        setProduct({
          merchName: merchProduct.title,
          merchImg:
            merchProduct?.sizes?.[0]?.images?.[0]?.src || mockupMerchImg,
        });
      }
    };

    if (artistId && productId) {
      loadProduct(artistId, productId);
    }

    return () => {
      isRendered = false;
    };
  }, [productId, artistId, orderId, goTo]);

  // TODO when we have order, show sample image

  return (
    <MotionOpacity className={props.className}>
      <SidebarContainer>
        <FormContainer wider>
          <WrapperContent
            flexDirection={props.isMobile ? 'column' : 'row'}
            gap={!props.isMobile ? 30 : 40}
          >
            {!!product?.merchImg && (
              <WrapperImg>
                <SquareImg className="artwork" srcImg={product.merchImg} />
              </WrapperImg>
            )}
            {!!product?.merchName && (
              <InfoWrapper>
                <div className="mb-40">
                  <Title customFont="semi-bold">
                    <Translate
                      id={
                        !!orderId
                          ? 'MERCH.ORDER_COMPLETED.TITLE.SAMPLE'
                          : 'MERCH.ORDER_COMPLETED.TITLE'
                      }
                      data={{ merchName: product.merchName }}
                    />
                  </Title>
                  <Text type="small">
                    <Translate
                      id={
                        !!orderId
                          ? 'MERCH.ORDER_COMPLETED.SUBTITLE.SAMPLE'
                          : 'MERCH.ORDER_COMPLETED.SUBTITLE'
                      }
                      data={{ merchName: product.merchName }}
                    />
                  </Text>
                </div>
                <LinkUnderline onClick={redirectCatalog}>
                  <Translate id="RELEASE.PUBLISHED.CONTINUE" />
                </LinkUnderline>
              </InfoWrapper>
            )}
          </WrapperContent>
        </FormContainer>
      </SidebarContainer>
    </MotionOpacity>
  );
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(MerchPublishSuccess);

const WrapperImg = styled.div`
  width: 325px;
  height: 325px;
  border-radius: 10px;
  background-color: ${({ theme }) => theme.colors.primary};

  ${({ theme }) => `@media screen and ${theme.device.tablet}`} {
    width: 298px;
    height: 298px;
  }

  .artwork {
    width: 327px;
    height: 327px;
    margin: 0 auto;
    display: block;
    ${({ theme }) => `@media screen and ${theme.device.tablet}`} {
      width: 300px;
      height: 300px;
      margin: 0;
    }
  }
`;

const WrapperContent = styled(Flex)`
  align-items: center;
  justify-content: center;

  ${({ theme }) => `@media screen and ${theme.device.tablet}`} {
    padding-block: 130px;
  }
`;

const Title = styled(Typography)`
  font-size: ${({ theme }) => theme.typography.h2};
  line-height: ${({ theme }) => theme.typography.h2};
  letter-spacing: -0.03em;
  margin-bottom: 20px;

  ${({ theme }) => `@media screen and ${theme.device.tablet}`} {
    font-size: ${({ theme }) => theme.typography.h1};
    line-height: ${({ theme }) => theme.typography.h1};
    margin-bottom: 40px;
  }
`;

const Text = styled(Typography)`
  color: ${({ theme }) => rgba(theme.colors.primary, 0.5)};
`;

const InfoWrapper = styled.div`
  ${({ theme }) => `@media screen and ${theme.device.tablet}`} {
    display: flex;
    flex-direction: column;
  }
`;

const LinkUnderline = styled(Typography)`
  width: max-content;
  margin: 0 auto;
  opacity: 1;
  text-align: center;
  cursor: pointer;
  border-bottom: 1px solid ${({ theme }) => theme.colors.primary};
  transition: opacity
    ${({ theme }) => `${theme.transitions.duration} ${theme.transitions.type}`};

  &:hover {
    opacity: 0.5;
  }

  ${({ theme }) => `@media screen and ${theme.device.tablet}`} {
    text-align: left;
    margin: 0 auto 0 0;
  }
`;
